// PDF导出工具 - 支持中文显示（优化分页，避免截断）
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

/**
 * 导出自检报告为PDF（支持中文，智能分页）
 * @param {Object} row - 自检数据行
 * @param {Object} context - Vue组件上下文（this）
 */
export async function exportSelfCheckToPDF(row, context) {
  try {
    // 创建PDF
    const doc = new jsPDF('p', 'mm', 'a4')
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 10
    let currentY = margin
    let isFirstPage = true

    // 辅助函数：渲染HTML片段为图片并添加到PDF
    async function addHtmlToPDF(htmlContent, maxHeight = null) {
      const container = document.createElement('div')
      container.style.position = 'fixed'
      container.style.left = '-9999px'
      container.style.top = '0'
      container.style.width = '800px'
      container.style.background = '#ffffff'
      container.style.padding = '20px'
      container.style.fontFamily = 'Arial, "Microsoft YaHei", sans-serif'
      container.innerHTML = htmlContent
      document.body.appendChild(container)

      await new Promise(resolve => setTimeout(resolve, 50))

      const canvas = await html2canvas(container, {
        scale: 2, // 分辨率
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false, // 关闭日志
        imageTimeout: 0
      })

      document.body.removeChild(container)

      const imgWidth = pageWidth - 2 * margin
      const imgHeight = (canvas.height * imgWidth) / canvas.width

      // 检查是否需要新页面
      if (!isFirstPage && currentY + imgHeight > pageHeight - margin) {
        doc.addPage()
        currentY = margin
      }

      // 使用JPEG格式并设置压缩质量，大幅减小文件大小
      const imgData = canvas.toDataURL('image/jpeg', 0.85) // JPEG格式，85%质量
      doc.addImage(imgData, 'JPEG', margin, currentY, imgWidth, imgHeight)
      currentY += imgHeight + 5 // 添加间距

      isFirstPage = false
      return imgHeight
    }

    // 1. 标题和基本信息
    const isZh = context.lang === 'zh'
    let headerHtml = '<div style="color: #000;">'
    headerHtml += '<h1 style="color: #0066cc; text-align: center; margin: 0 0 15px 0; font-size: 28px;">' + (isZh ? '机器人自检报告' : 'Robot Self-Check Report') + '</h1>'
    headerHtml += '<hr style="border: none; border-top: 2px solid #0066cc; margin: 0 0 15px 0;" />'
    headerHtml += '<div style="font-size: 14px; line-height: 1.8;">'
    headerHtml += '<p style="margin: 5px 0;"><strong>' + (isZh ? '机器人编号：' : 'Robot Code: ') + '</strong>' + (row.robotCode || '-') + '</p>'
    headerHtml += '<p style="margin: 5px 0;"><strong>' + (isZh ? '自检时间：' : 'Check Time: ') + '</strong>' + context.formatDateTime(row.checkTime) + '</p>'
    headerHtml += '<p style="margin: 5px 0;"><strong>' + (isZh ? '触发类型：' : 'Trigger Type: ') + '</strong>' + context.getTypeText(row.type) + '</p>'
    headerHtml += '<p style="margin: 5px 0;"><strong>' + (isZh ? '自检状态：' : 'Status: ') + '</strong>' + context.getStatusText(row.status) + '</p>'
    headerHtml += '</div></div>'
    await addHtmlToPDF(headerHtml)

    // 2. 自检结果摘要
    let summaryHtml = '<div style="color: #000;">'
    summaryHtml += '<h2 style="color: #0066cc; font-size: 18px; margin: 15px 0 10px 0;">' + (isZh ? '自检结果摘要' : 'Result Summary') + '</h2>'
    summaryHtml += '<div style="font-size: 13px; line-height: 1.6;">'
    if (row.resultBadges && row.resultBadges.length > 0) {
      row.resultBadges.forEach(badge => {
        const colorMap = {
          'ok': '#39FF14',
          'warn': '#FFD700',
          'error': '#FF8C00',
          'fatal': '#FF4444'
        }
        const color = colorMap[badge.type] || '#666'
        summaryHtml += '<p style="margin: 5px 0;"><span style="color: ' + color + '; font-weight: bold;">' + badge.label + ':</span> ' + badge.count + '</p>'
      })
    } else {
      summaryHtml += '<p>' + (isZh ? '暂无数据' : 'No data') + '</p>'
    }
    summaryHtml += '</div></div>'
    await addHtmlToPDF(summaryHtml)

    // 3. 详细检查项标题
    if (row.items && row.items.length > 0) {
      let titleHtml = '<div style="color: #000;">'
      titleHtml += '<h2 style="color: #0066cc; font-size: 18px; margin: 15px 0 10px 0;">' + (isZh ? '详细检查项' : 'Detailed Check Items') + '</h2>'
      titleHtml += '</div>'
      await addHtmlToPDF(titleHtml)

      // 4. 逐个渲染检查项（避免截断）
      for (let index = 0; index < row.items.length; index++) {
        const item = row.items[index]
        const levelColors = {
          0: '#39FF14',
          1: '#FFD700',
          2: '#FF8C00',
          3: '#FF4444'
        }
        const levelColor = levelColors[item.level] || '#666'
        const levelText = context.getLevelText(item.level)

        let itemHtml = '<div style="color: #000;">'
        itemHtml += '<div style="margin: 10px 0; padding: 15px; background: #f5f5f5; border-left: 4px solid ' + levelColor + '; border-radius: 4px; page-break-inside: avoid;">'
        itemHtml += '<h3 style="margin: 0 0 10px 0; font-size: 15px; color: #333;">' + (index + 1) + '. ' + (isZh ? (item.deviceNameCn || item.name) : (item.deviceNameEn || item.name)) + '</h3>'
        itemHtml += '<p style="margin: 5px 0; font-size: 12px;"><strong style="color: ' + levelColor + ';">' + (isZh ? '级别：' : 'Level: ') + '</strong>' + levelText + '</p>'
        itemHtml += '<p style="margin: 5px 0; font-size: 12px;"><strong>' + (isZh ? '设备ID：' : 'Device ID: ') + '</strong>' + item.id + '</p>'

        if (item.values && item.values.length > 0) {
          itemHtml += '<div style="margin-top: 10px; font-size: 12px;"><strong>' + (isZh ? '参数：' : 'Parameters: ') + '</strong></div>'
          itemHtml += '<ul style="margin: 5px 0; padding-left: 20px; font-size: 11px; line-height: 1.6;">'

          item.values.forEach(val => {
            const isErrorCode = val.key && val.key.toString().startsWith('0x')

            if (isErrorCode) {
              const errorInfo = context.errorCodeDict[val.key]
              const errorDesc = errorInfo
                ? (isZh ? errorInfo.errNameCn : errorInfo.errNameEn)
                : val.value
              itemHtml += '<li style="color: #FF4444; margin: 3px 0;"><strong>' + (isZh ? '错误码：' : 'Error Code: ') + '</strong>' + val.key + ' - ' + errorDesc + '</li>'
            } else {
              itemHtml += '<li style="margin: 3px 0;">' + context.translateKey(val.key) + ': ' + context.translateValue(val.key, val.value) + '</li>'
            }
          })

          itemHtml += '</ul>'
        }

        itemHtml += '</div></div>'

        // 检查当前项的高度，如果太高可能需要分页
        await addHtmlToPDF(itemHtml)
      }
    }

    // 5. 页脚（使用数字格式避免中文乱码）
    const totalPages = doc.internal.getNumberOfPages()
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.setTextColor(128, 128, 128)
      // 使用简洁的数字格式：1 / 5
      const pageText = `${i} / ${totalPages}`
      doc.text(pageWidth / 2, pageHeight - 5, pageText, { align: 'center' })
      doc.text(pageWidth - margin, pageHeight - 5, new Date().toLocaleString(isZh ? 'zh-CN' : 'en-US'), { align: 'right' })
    }

    // 保存PDF
    // 格式化时间为 20260512_154737 格式
    let timeStr = ''
    if (row.checkTime) {
      const date = new Date(row.checkTime)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      timeStr = `${year}${month}${day}_${hours}${minutes}${seconds}`
    } else {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const seconds = String(now.getSeconds()).padStart(2, '0')
      timeStr = `${year}${month}${day}_${hours}${minutes}${seconds}`
    }
    const fileName = (isZh ? '自检报告_' : 'SelfCheck_Report_') + timeStr + '.pdf'
    doc.save(fileName)

    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}
